import {
  Button,
  CircularProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
  IconButton,
  Menu,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Divider,
  Box,
  Chip,
  Typography,
  Tooltip,
  Paper,
  Pagination,
  Drawer,
  Collapse,
  TextField,
  InputAdornment,
  alpha,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import { useApolloClient } from "@apollo/client";
import PaginationItem from "@mui/material/PaginationItem";
import type { PaginationRenderItemParams } from "@mui/material/Pagination";
import type { ChipProps } from "@mui/material/Chip";
import { useGetAllDesignerAccountsQuery } from "../../../hooks/useGetAllDesignerAccountsQuery";
import { useUpdateDesignerAccountMutation } from "../../../hooks/useUpdateDesignerAccountMutation";
import { useUpdateDesignerApplicationMutation } from "../../../hooks/useUpdateDesignerApplicationMutation";
import { SectionEditForm } from "./SectionEditForm";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  BusinessGoals,
  DesignerApplicationSteps,
  DesignerUser,
  PopulatedDesignerApplication,
  Reference,
  WorkExperience,
} from "../../../__generated__/types";
import { useDesignerApplicationContext } from "../../../hooks/designer-application-context";
import { ErrorMessage } from "../../../components/error-message";
import { searchDesignerApplications } from "../../../utils/search-utils";
import { useNotification } from "../../../contexts/NotificationContext";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import { useUserContext } from "../../../hooks/userContext";
import { isAdminUser, Pages } from "../../../utils";
import { useNavigate } from "react-router";
import ViewColumnIcon from "@mui/icons-material/ViewColumn";
import DeleteIcon from "@mui/icons-material/DeleteOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import WorkOutlineOutlinedIcon from "@mui/icons-material/WorkOutlineOutlined";
import SchoolOutlinedIcon from "@mui/icons-material/SchoolOutlined";
import ContactPageOutlinedIcon from "@mui/icons-material/ContactPageOutlined";
import FlagOutlinedIcon from "@mui/icons-material/FlagOutlined";
import PersonOutlinedIcon from "@mui/icons-material/PersonOutlined";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SearchIcon from "@mui/icons-material/Search";
import VisibilityIcon from "@mui/icons-material/Visibility";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";

// ---- Local helper: allow optional date fields without changing generated types
type AppWithDates = PopulatedDesignerApplication & {
  createdAt?: string | Date | null;
  updatedAt?: string | Date | null;
  submittedAt?: string | Date | null;
};

const PAGE_SIZE = 10;

type StatusFilterValue = "ALL" | DesignerApplicationSteps;
type SortableColumn = "state" | "accountCreated" | "submittedDate";

// Define available columns
interface ColumnConfig {
  id: string;
  label: string;
  visible: boolean;
  minWidth?: number;
  align?: "left" | "center" | "right";
}

const defaultColumns: ColumnConfig[] = [
  { id: "designerName", label: "Designer Name", visible: true, minWidth: 150 },
  { id: "status", label: "Status", visible: true, minWidth: 140 },
  { id: "subscription", label: "Subscription", visible: true, minWidth: 120 },
  { id: "submittedDate", label: "Date", visible: true, minWidth: 120 },
  { id: "accountCreated", label: "Account Created", visible: false, minWidth: 120 },
  { id: "companyName", label: "Company Name", visible: true, minWidth: 150 },
  { id: "city", label: "City", visible: true, minWidth: 100 },
  { id: "state", label: "State", visible: true, minWidth: 80 },
  { id: "email", label: "Email", visible: false, minWidth: 200 },
  { id: "phone", label: "Phone", visible: false, minWidth: 120 },
  { id: "yearsExperience", label: "Years Experience", visible: false, minWidth: 80, align: "center" },
  { id: "portfolioUrl", label: "Portfolio", visible: false, minWidth: 100 },
  { id: "actions", label: "Actions", visible: true, minWidth: 200, align: "center" },
];

const statusLabels: Record<DesignerApplicationSteps, string> = {
  [DesignerApplicationSteps.ToBeCompleted]: "Account Created",
  [DesignerApplicationSteps.UnderReview]: "Under Review",
  [DesignerApplicationSteps.Approved]: "Approved",
  [DesignerApplicationSteps.CompleteApplication]: "Application In Progress",
  [DesignerApplicationSteps.MembershipActive]: "Membership Active",
  [DesignerApplicationSteps.Rejected]: "Rejected",
};

const statusChipColors: Record<DesignerApplicationSteps, ChipProps["color"]> = {
  [DesignerApplicationSteps.ToBeCompleted]: "info",
  [DesignerApplicationSteps.UnderReview]: "warning",
  [DesignerApplicationSteps.Approved]: "success",
  [DesignerApplicationSteps.CompleteApplication]: "default",
  [DesignerApplicationSteps.MembershipActive]: "primary",
  [DesignerApplicationSteps.Rejected]: "error",
};

const STATUS_FILTERS: Array<{
  value: StatusFilterValue;
  label: string;
  color: ChipProps["color"];
}> = [
  { value: "ALL", label: "All", color: "default" },
  {
    value: DesignerApplicationSteps.ToBeCompleted,
    label: statusLabels[DesignerApplicationSteps.ToBeCompleted],
    color: statusChipColors[DesignerApplicationSteps.ToBeCompleted],
  },
  {
    value: DesignerApplicationSteps.UnderReview,
    label: statusLabels[DesignerApplicationSteps.UnderReview],
    color: statusChipColors[DesignerApplicationSteps.UnderReview],
  },
  {
    value: DesignerApplicationSteps.CompleteApplication,
    label: statusLabels[DesignerApplicationSteps.CompleteApplication],
    color: statusChipColors[DesignerApplicationSteps.CompleteApplication],
  },
  {
    value: DesignerApplicationSteps.Approved,
    label: statusLabels[DesignerApplicationSteps.Approved],
    color: statusChipColors[DesignerApplicationSteps.Approved],
  },
  {
    value: DesignerApplicationSteps.MembershipActive,
    label: statusLabels[DesignerApplicationSteps.MembershipActive],
    color: statusChipColors[DesignerApplicationSteps.MembershipActive],
  },
  {
    value: DesignerApplicationSteps.Rejected,
    label: statusLabels[DesignerApplicationSteps.Rejected],
    color: statusChipColors[DesignerApplicationSteps.Rejected],
  },
];

const STATUS_ORDER: DesignerApplicationSteps[] = [
  DesignerApplicationSteps.ToBeCompleted,
  DesignerApplicationSteps.UnderReview,
  DesignerApplicationSteps.CompleteApplication,
  DesignerApplicationSteps.Approved,
  DesignerApplicationSteps.Rejected,
  DesignerApplicationSteps.MembershipActive,
];

type PaginationSlotProps = React.HTMLAttributes<HTMLSpanElement> & { ownerState?: unknown };

const PrevPaginationSlot = React.forwardRef<HTMLSpanElement, PaginationSlotProps>(
  ({ className, ownerState: _ownerState, ...rest }, ref) => (
    <span ref={ref} className={className} {...rest}>
      <Stack direction="row" spacing={0.75} alignItems="center" justifyContent="center">
        <ChevronLeftIcon sx={{ fontSize: 18 }} />
        <Typography variant="body2" fontWeight={500} color="inherit">
          Previous
        </Typography>
      </Stack>
    </span>
  )
);
PrevPaginationSlot.displayName = "PrevPaginationSlot";

const NextPaginationSlot = React.forwardRef<HTMLSpanElement, PaginationSlotProps>(
  ({ className, ownerState: _ownerState, ...rest }, ref) => (
    <span ref={ref} className={className} {...rest}>
      <Stack direction="row" spacing={0.75} alignItems="center" justifyContent="center">
        <Typography variant="body2" fontWeight={500} color="inherit">
          Next
        </Typography>
        <ChevronRightIcon sx={{ fontSize: 18 }} />
      </Stack>
    </span>
  )
);
NextPaginationSlot.displayName = "NextPaginationSlot";

export const ApplicationTable = () => {
  const [allApplications, setAllApplications] = useState<PopulatedDesignerApplication[]>([]);
  const [selectedApplication, setSelectedApplication] = useState<AppWithDates | null>(null);
  const [selectedStatus, setSelectedStatus] = useState<StatusFilterValue>("ALL");
  const [error, setError] = useState<string | null>(null);
  const [columns, setColumns] = useState<ColumnConfig[]>(defaultColumns);
  const [columnMenuAnchor, setColumnMenuAnchor] = useState<null | HTMLElement>(null);
  const [page, setPage] = useState<number>(1);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [expandedSection, setExpandedSection] = useState<string | null>("experience");
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [sortConfig, setSortConfig] = useState<
    { column: SortableColumn; direction: "asc" | "desc" } | null
  >(null);
  const [deleteDialog, setDeleteDialog] = useState<{
    open: boolean;
    type: "application" | "account" | null;
    applicationId: string | null;
    applicationName: string | null;
  }>({
    open: false,
    type: null,
    applicationId: null,
    applicationName: null,
  });
  const [editingSection, setEditingSection] = useState<string | null>(null);
  const [editFormData, setEditFormData] = useState<{
    // User fields
    firstName: string;
    lastName: string;
    email: string;
    phoneNumber: string;
    businessName: string;
    businessWebsite?: string;
    businessAddress?: string;
    businessCity?: string;
    businessState?: string;
    businessZipCode?: string;
    // Application fields
    education: string[];
    experiences: Array<{
      id?: string;
      employmentType?: string;
      title?: string;
      company?: string;
      startDate?: string;
      endDate?: string;
      current?: boolean;
      firmSize?: string;
    }>;
    references: Array<{
      id?: string;
      firstName?: string;
      lastName?: string;
      title?: string;
      type?: string;
      businessName?: string;
      email?: string;
      phoneNumber?: string;
      startDate?: string;
      endDate?: string;
      yearsKnown?: number;
      ProjectType?: string;
    }>;
    businessGoals: {
      challenges?: string;
      expectations?: string;
      overview?: {
        numberOfProjectsCurrentYear?: number;
        numberOfProjectsLastYear?: number;
        totalRevenueCurrentYear?: string;
        totalRevenueLastYear?: string;
      };
      projectCapacity?: {
        largeScale?: string;
        smallScale?: string;
      };
    };
  }>({
    firstName: "",
    lastName: "",
    email: "",
    phoneNumber: "",
    businessName: "",
    businessWebsite: "",
    businessAddress: "",
    businessCity: "",
    businessState: "",
    businessZipCode: "",
    education: [],
    experiences: [],
    references: [],
    businessGoals: {
      challenges: "",
      expectations: "",
      overview: {
        numberOfProjectsCurrentYear: undefined,
        numberOfProjectsLastYear: undefined,
        totalRevenueCurrentYear: "",
        totalRevenueLastYear: "",
      },
      projectCapacity: {
        largeScale: "",
        smallScale: "",
      },
    },
  });
  const theme = useTheme();

  const navigate = useNavigate();
  const { user } = useUserContext();

  const {
    getAllDesignerAccounts,
    loading,
    error: designerListError,
  } = useGetAllDesignerAccountsQuery();

  const { updateDesignerAccount, loading: updateAccountLoading } = useUpdateDesignerAccountMutation();
  const { updateDesignerApplication, loading: updateApplicationLoading } = useUpdateDesignerApplicationMutation();
  const updateLoading = updateAccountLoading || updateApplicationLoading;
  const { showSuccess, showError, showNotification } = useNotification();
  const apolloClient = useApolloClient();

  const getUserForApp = (app: any): DesignerUser | undefined => {
    // With the new unified query, designerId is always populated
    return app?.designerId as DesignerUser;
  };

  const loadAllApplications = useCallback(
    async (refresh: boolean = false) => {
      setError(null);
      try {
        const list = await getAllDesignerAccounts(refresh);
        setAllApplications(list ?? []);
        return list ?? []; // Return the updated applications list
      } catch (err: any) {
        console.error("Error details:", {
          message: err?.message,
          graphQLErrors: err?.graphQLErrors,
          networkError: err?.networkError,
          stack: err?.stack
        });
        setError(err?.message ?? "Failed to load designer accounts");
        setAllApplications([]);
        return []; // Return empty array on error
      }
    },
    [getAllDesignerAccounts]
  );

  const { deleteDesignerAccount, deleteDesignerApplication } =
    useDesignerApplicationContext();

  const handleStatusChange = async (application: PopulatedDesignerApplication, newStatus: DesignerApplicationSteps) => {
    try {
      await updateDesignerApplication({
        _id: (application as any)._id,
        status: newStatus,
      });
      
      showSuccess(`Application status updated to ${statusLabels[newStatus]}`);
      
      // Refresh the data
      await loadAllApplications(true);
    } catch (error) {
      showError('Failed to update application status');
    }
  };


  const handleDeleteAccount = (applicationId: string, applicationName: string) => {
    setDeleteDialog({
      open: true,
      type: "account",
      applicationId,
      applicationName,
    });
  };

  const handleConfirmDelete = async () => {
    if (!deleteDialog.applicationId || !deleteDialog.type) return;

    try {
      // Check if this is a bulk delete (multiple IDs separated by commas)
      const isBulkDelete = deleteDialog.applicationId.includes(",");
      
      if (isBulkDelete) {
        // Handle bulk delete - determine delete function for each ID
        const ids = deleteDialog.applicationId.split(",");
        const results = await Promise.allSettled(
          ids.map(id => {
            const app = allApplications.find(a => getRowId(a) === id);
            if (app) {
              const designerId = getDesignerId(app);
              if (designerId) {
                // Has valid designer ID, delete account (which includes applications)
                return deleteDesignerAccount(designerId);
              } else {
                // No valid designer ID, delete just the application
                return deleteDesignerApplication(getRowId(app));
              }
            }
            return Promise.resolve({ error: "Application not found" });
          })
        );
        
        const errors = results
          .map((result, index) => {
            if (result.status === "rejected") {
              return `Failed to delete application ${ids[index]}: ${result.reason}`;
            }
            if (result.status === "fulfilled" && typeof result.value === "object" && result.value?.error) {
              return `Failed to delete application ${ids[index]}: ${result.value.error}`;
            }
            return null;
          })
          .filter(Boolean);
        
        if (errors.length > 0) {
          setError(errors.join("; "));
        } else {
          await loadAllApplications(true);
          setSelectedIds(new Set()); // Clear selection after successful bulk delete
        }
      } else {
        // Handle single delete - determine delete function based on designerId
        const app = allApplications.find(a => getRowId(a) === deleteDialog.applicationId);
        if (app) {
          const designerId = getDesignerId(app);
          let result;
          
          if (designerId) {
            // Has valid designer ID, delete account (which includes applications)
            result = await deleteDesignerAccount(designerId);
          } else {
            // No valid designer ID, delete just the application
            result = await deleteDesignerApplication(getRowId(app));
          }

          if (typeof result === "object" && result?.error) {
            setError(result.error);
          } else {
            await loadAllApplications(true);
          }
        } else {
          setError("Application not found");
        }
      }
    } catch (error) {
      setError("Failed to delete");
    } finally {
      setDeleteDialog({
        open: false,
        type: null,
        applicationId: null,
        applicationName: null,
      });
    }
  };

  const handleCancelDelete = () => {
    setDeleteDialog({
      open: false,
      type: null,
      applicationId: null,
      applicationName: null,
    });
  };

  const handleSectionEditClick = (sectionId: string) => {
    if (selectedApplication) {
      const user = getUserForApp(selectedApplication);
      const experiences = (selectedApplication?.experience ?? []).filter(Boolean) as WorkExperience[];
      const educationList = (selectedApplication?.education ?? []).filter(
        (item): item is string => typeof item === "string" && item.trim().length > 0
      );
      const references = (selectedApplication?.references ?? []).filter(Boolean) as Reference[];
      const businessGoals = selectedApplication?.businessGoals as BusinessGoals | null | undefined;

      setEditFormData({
        firstName: user?.firstName || "",
        lastName: user?.lastName || "",
        email: user?.email || "",
        phoneNumber: user?.phoneNumber || "",
        businessName: user?.businessName || "",
        businessWebsite: (user as any)?.businessWebsite || "",
        businessAddress: (user as any)?.businessAddress || "",
        businessCity: (user as any)?.businessCity || "",
        businessState: (user as any)?.businessState || "",
        businessZipCode: (user as any)?.businessZipCode || "",
        education: educationList,
        experiences: experiences.map(exp => ({
          id: exp.id || undefined,
          employmentType: exp.employmentType || undefined,
          title: exp.title || undefined,
          company: exp.company || undefined,
          startDate: exp.startDate || undefined,
          endDate: exp.endDate || undefined,
          current: exp.current || undefined,
          firmSize: exp.firmSize || undefined,
        })),
        references: references.map(ref => ({
          id: ref.id || undefined,
          firstName: ref.firstName || undefined,
          lastName: ref.lastName || undefined,
          title: ref.title || undefined,
          type: ref.type || undefined,
          businessName: ref.businessName || undefined,
          email: ref.email || undefined,
          phoneNumber: ref.phoneNumber || undefined,
          startDate: ref.startDate || undefined,
          endDate: ref.endDate || undefined,
          yearsKnown: ref.yearsKnown || undefined,
          ProjectType: (ref as any)?.ProjectType || undefined,
        })),
        businessGoals: {
          challenges: businessGoals?.challenges || "",
          expectations: businessGoals?.expectations || "",
          overview: {
            numberOfProjectsCurrentYear: businessGoals?.overview?.numberOfProjectsCurrentYear || undefined,
            numberOfProjectsLastYear: businessGoals?.overview?.numberOfProjectsLastYear || undefined,
            totalRevenueCurrentYear: businessGoals?.overview?.totalRevenueCurrentYear || "",
            totalRevenueLastYear: businessGoals?.overview?.totalRevenueLastYear || "",
          },
          projectCapacity: {
            largeScale: businessGoals?.projectCapacity?.largeScale || "",
            smallScale: businessGoals?.projectCapacity?.smallScale || "",
          },
        },
      });
      setEditingSection(sectionId);
    }
  };

  const handleSectionEditCancel = () => {
    setEditingSection(null);
    setEditFormData({
      firstName: "",
      lastName: "",
      email: "",
      phoneNumber: "",
      businessName: "",
      education: [],
      experiences: [],
      references: [],
      businessGoals: {
        challenges: "",
        expectations: "",
        overview: {
          numberOfProjectsCurrentYear: undefined,
          numberOfProjectsLastYear: undefined,
          totalRevenueCurrentYear: "",
          totalRevenueLastYear: "",
        },
        projectCapacity: {
          largeScale: "",
          smallScale: "",
        },
      },
    });
  };

  const handleSectionEditSave = async () => {
    if (!selectedApplication || !editingSection) return;

    try {
      const designerId = getDesignerId(selectedApplication);
      const applicationId = getRowId(selectedApplication);
      const hasApplication = selectedApplication.status !== DesignerApplicationSteps.ToBeCompleted;

      // Update designer account (if we have a valid designerId and editing basic info)
      if (designerId && editingSection === 'basic') {
        await updateDesignerAccount({
          variables: {
            _id: designerId,
            firstName: editFormData.firstName,
            lastName: editFormData.lastName,
            email: editFormData.email,
            phoneNumber: editFormData.phoneNumber,
            businessName: editFormData.businessName,
            businessWebsite: editFormData.businessWebsite,
            businessAddress: editFormData.businessAddress,
            businessCity: editFormData.businessCity,
            // Convert empty string to null for enum fields (GraphQL doesn't accept empty strings for enums)
            businessState: editFormData.businessState && editFormData.businessState.trim() !== '' 
              ? (editFormData.businessState as any) 
              : null,
            businessZipCode: editFormData.businessZipCode,
          },
        });
      }

      // Only update application data if:
      // 1. We're NOT editing basic info (basic info is handled above)
      // 2. AND an application actually exists (status is not "ToBeCompleted")
      if (editingSection !== 'basic' && hasApplication) {
        await updateDesignerApplication({
        _id: applicationId,
        education: editFormData.education,
        experience: editFormData.experiences.map(exp => ({
          id: exp.id,
          employmentType: exp.employmentType as any, // Cast to handle enum types
          title: exp.title,
          company: exp.company,
          startDate: exp.startDate,
          endDate: exp.endDate,
          current: exp.current,
          firmSize: exp.firmSize as any, // Cast to handle enum types
        })),
        references: editFormData.references.map(ref => ({
          id: ref.id,
          firstName: ref.firstName,
          lastName: ref.lastName,
          title: ref.title,
          type: ref.type as any, // Cast to handle enum types
          businessName: ref.businessName,
          email: ref.email,
          phoneNumber: ref.phoneNumber,
          startDate: ref.startDate,
          endDate: ref.endDate,
          yearsKnown: ref.yearsKnown,
          ProjectType: ref.ProjectType as any, // Cast to handle enum types
        })),
        businessGoals: {
          challenges: editFormData.businessGoals.challenges,
          expectations: editFormData.businessGoals.expectations,
          overview: {
            numberOfProjectsCurrentYear: editFormData.businessGoals.overview?.numberOfProjectsCurrentYear,
            numberOfProjectsLastYear: editFormData.businessGoals.overview?.numberOfProjectsLastYear,
            totalRevenueCurrentYear: editFormData.businessGoals.overview?.totalRevenueCurrentYear as any, // Cast to handle enum types
            totalRevenueLastYear: editFormData.businessGoals.overview?.totalRevenueLastYear as any, // Cast to handle enum types
          },
          projectCapacity: {
            largeScale: editFormData.businessGoals.projectCapacity?.largeScale && editFormData.businessGoals.projectCapacity.largeScale !== "" 
              ? editFormData.businessGoals.projectCapacity.largeScale as any 
              : null, // Handle empty strings by setting to null
            smallScale: editFormData.businessGoals.projectCapacity?.smallScale && editFormData.businessGoals.projectCapacity.smallScale !== "" 
              ? editFormData.businessGoals.projectCapacity.smallScale as any 
              : null, // Handle empty strings by setting to null
          },
        },
        });
      }

      // Show success message
      showSuccess('Application updated successfully');
      
      // Invalidate Apollo cache to force fresh data
      await apolloClient.refetchQueries({
        include: ['GetAllDesignerAccounts']
      });
      
      // Force refresh the applications list to show updated data
      const updatedApplications = await loadAllApplications(true);
      
      // Update the selected application with the fresh data
      if (selectedApplication && updatedApplications) {
        const updatedApplication = updatedApplications.find((app: any) => 
          getRowId(app) === getRowId(selectedApplication)
        );
        if (updatedApplication) {
          setSelectedApplication(updatedApplication);
        }
      }
      
      setEditingSection(null);
      setError(null);
      
      // Show success notification
      const sectionName = editingSection === 'basic' ? 'Basic Information' : 
                         editingSection === 'experience' ? 'Experience' :
                         editingSection === 'education' ? 'Education' :
                         editingSection === 'references' ? 'References' :
                         editingSection === 'businessGoals' ? 'Business Goals' : 'Information';
      
      showSuccess(`${sectionName} successfully updated!`, {
        duration: 4000,
        animation: 'bounce'
      });
    } catch (error: any) {
      setError(error?.message || "Failed to update designer information");
      showError(error?.message || "Failed to update designer information", {
        duration: 6000
      });
    }
  };

  const handleEditFormChange = (field: string, value: string) => {
    setEditFormData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleEditFormChangeNested = (field: string, subField: string, value: any) => {
    setEditFormData(prev => ({
      ...prev,
      [field]: {
        ...(prev[field as keyof typeof prev] as any),
        [subField]: value,
      },
    }));
  };

  const handleEditFormChangeDeep = (field: string, subField: string, subSubField: string, value: any) => {
    setEditFormData(prev => ({
      ...prev,
      [field]: {
        ...(prev[field as keyof typeof prev] as any),
        [subField]: {
          ...((prev[field as keyof typeof prev] as any)?.[subField] || {}),
          [subSubField]: value,
        },
      },
    }));
  };

  const addEducationEntry = () => {
    setEditFormData(prev => ({
      ...prev,
      education: [...prev.education, ""],
    }));
  };

  const removeEducationEntry = (index: number) => {
    setEditFormData(prev => ({
      ...prev,
      education: prev.education.filter((_, i) => i !== index),
    }));
  };

  const updateEducationEntry = (index: number, value: string) => {
    setEditFormData(prev => ({
      ...prev,
      education: prev.education.map((item, i) => i === index ? value : item),
    }));
  };

  const addExperienceEntry = () => {
    setEditFormData(prev => ({
      ...prev,
      experiences: [...prev.experiences, {
        id: "",
        employmentType: "",
        title: "",
        company: "",
        startDate: "",
        endDate: "",
        current: false,
        firmSize: "",
      }],
    }));
  };

  const removeExperienceEntry = (index: number) => {
    setEditFormData(prev => ({
      ...prev,
      experiences: prev.experiences.filter((_, i) => i !== index),
    }));
  };

  const updateExperienceEntry = (index: number, field: string, value: any) => {
    setEditFormData(prev => ({
      ...prev,
      experiences: prev.experiences.map((item, i) => 
        i === index ? { ...item, [field]: value } : item
      ),
    }));
  };

  const addReferenceEntry = () => {
    setEditFormData(prev => ({
      ...prev,
      references: [...prev.references, {
        id: "",
        firstName: "",
        lastName: "",
        title: "",
        type: "",
        businessName: "",
        email: "",
        phoneNumber: "",
        startDate: "",
        endDate: "",
        yearsKnown: 0,
        ProjectType: "",
      }],
    }));
  };

  const removeReferenceEntry = (index: number) => {
    setEditFormData(prev => ({
      ...prev,
      references: prev.references.filter((_, i) => i !== index),
    }));
  };

  const updateReferenceEntry = (index: number, field: string, value: any) => {
    setEditFormData(prev => ({
      ...prev,
      references: prev.references.map((item, i) => 
        i === index ? { ...item, [field]: value } : item
      ),
    }));
  };

  const handleColumnToggle = (columnId: string) => {
    setColumns((prev) =>
      prev.map((col) => (col.id === columnId ? { ...col, visible: !col.visible } : col))
    );
  };

  const handleApplyColumns = () => {
    setColumnMenuAnchor(null);
    // Optionally save to localStorage or user preferences
    localStorage.setItem("adminTableColumns", JSON.stringify(columns));
  };

  useEffect(() => {
    loadAllApplications();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const statusCounts = useMemo(() => {
    const counts: Record<StatusFilterValue, number> = {
      ALL: allApplications.length,
      [DesignerApplicationSteps.ToBeCompleted]: 0,
      [DesignerApplicationSteps.UnderReview]: 0,
      [DesignerApplicationSteps.CompleteApplication]: 0,
      [DesignerApplicationSteps.Approved]: 0,
      [DesignerApplicationSteps.Rejected]: 0,
      [DesignerApplicationSteps.MembershipActive]: 0,
    };

    allApplications.forEach((app) => {
      const status = app.status as DesignerApplicationSteps | null | undefined;
      if (status && STATUS_ORDER.includes(status)) {
        counts[status] += 1;
      }
    });

    return counts;
  }, [allApplications]);

  const filteredApplications = useMemo(() => {
    if (!allApplications.length) return [];

    // First filter by status
    const statusFiltered = allApplications.filter((app) => {
      const statusMatches =
        selectedStatus === "ALL" ||
        (app.status as DesignerApplicationSteps | null | undefined) === selectedStatus;
      return statusMatches;
    });

    // Then apply comprehensive search
    return searchDesignerApplications(statusFiltered, searchTerm);
  }, [allApplications, selectedStatus, searchTerm]);

  const getStateSortValue = (app: PopulatedDesignerApplication) => {
    const user = getUserForApp(app);
    const state = (user as any)?.businessState ?? "";
    return typeof state === "string" ? state.toLowerCase() : String(state || "").toLowerCase();
  };

  const getNameSortValue = (app: PopulatedDesignerApplication) => {
    const user = getUserForApp(app);
    const first = user?.firstName ?? "";
    const last = user?.lastName ?? "";
    return `${first} ${last}`.trim().toLowerCase();
  };

  const getDateSortValue = (app: PopulatedDesignerApplication, column: "accountCreated" | "submittedDate") => {
    const appWithDates = app as AppWithDates;
    if (column === "accountCreated") {
      // Prioritize profile creation date (user.createdAt) over application creation date
      const user = getUserForApp(app);
      const profileCreated = user?.createdAt;
      const date = profileCreated || appWithDates.createdAt;
      return date ? new Date(date).getTime() : 0;
    } else if (column === "submittedDate") {
      const date = appWithDates.submittedAt ?? appWithDates.createdAt ?? appWithDates.updatedAt;
      return date ? new Date(date).getTime() : 0;
    }
    return 0;
  };

  const sortedApplications = useMemo(() => {
    if (!sortConfig) {
      return filteredApplications;
    }

    const data = [...filteredApplications];
    data.sort((a, b) => {
      if (sortConfig.column === "state") {
        const aState = getStateSortValue(a);
        const bState = getStateSortValue(b);

        if (aState === bState) {
          const aName = getNameSortValue(a);
          const bName = getNameSortValue(b);
          const nameCompare = aName.localeCompare(bName);
          return sortConfig.direction === "asc" ? nameCompare : -nameCompare;
        }

        const comparison = aState.localeCompare(bState);
        return sortConfig.direction === "asc" ? comparison : -comparison;
      } else if (sortConfig.column === "accountCreated" || sortConfig.column === "submittedDate") {
        const aDate = getDateSortValue(a, sortConfig.column);
        const bDate = getDateSortValue(b, sortConfig.column);
        const comparison = aDate - bDate;
        return sortConfig.direction === "asc" ? comparison : -comparison;
      }

      return 0;
    });

    return data;
  }, [filteredApplications, sortConfig]);

  useEffect(() => {
    if (!isAdminUser(user)) {
      navigate(Pages.DASHBOARD);
    }
  }, [user, navigate]);

  useEffect(() => {
    setPage(1);
    setSelectedIds(new Set());
  }, [selectedStatus]);

  const pageCount = useMemo(
    () => Math.max(1, Math.ceil(sortedApplications.length / PAGE_SIZE)),
    [sortedApplications.length]
  );

  useEffect(() => {
    if (page > pageCount) {
      setPage(pageCount);
    }
  }, [page, pageCount]);

  const visibleRows = useMemo(
    () =>
      sortedApplications.slice(
        (page - 1) * PAGE_SIZE,
        (page - 1) * PAGE_SIZE + PAGE_SIZE
      ),
    [sortedApplications, page]
  );

  useEffect(() => {
    if (selectedApplication) {
      setExpandedSection("experience");
    }
  }, [selectedApplication]);

  // Load saved column preferences
  useEffect(() => {
    const savedColumns = localStorage.getItem("adminTableColumns");
    if (savedColumns) {
      try {
        const parsed: ColumnConfig[] = JSON.parse(savedColumns);
        const merged = defaultColumns.map((defaultColumn) => {
          const saved = parsed.find((col) => col.id === defaultColumn.id);
          return saved ? { ...defaultColumn, ...saved } : defaultColumn;
        });
        const extras = parsed.filter(
          (saved) => !merged.some((column) => column.id === saved.id)
        );
        setColumns([...merged, ...extras]);
      } catch (err) {
        setColumns(defaultColumns);
      }
    }
  }, []);


  const statusBadgeStyles = useMemo(
    () => ({
      [DesignerApplicationSteps.ToBeCompleted]: {
        bg: alpha(theme.palette.info.main, 0.08),
        color: theme.palette.info.main,
      },
      [DesignerApplicationSteps.Approved]: {
        bg: alpha(theme.palette.success.main, 0.10),
        color: theme.palette.success.main,
      },
      [DesignerApplicationSteps.UnderReview]: {
        bg: alpha(theme.palette.warning.main, 0.10),
        color: theme.palette.warning.dark ?? theme.palette.warning.main,
      },
      [DesignerApplicationSteps.CompleteApplication]: {
        bg: alpha(theme.palette.grey[500], 0.08),
        color: theme.palette.grey[700],
      },
      [DesignerApplicationSteps.MembershipActive]: {
        bg: alpha(theme.palette.primary.main, 0.10),
        color: theme.palette.primary.main,
      },
      [DesignerApplicationSteps.Rejected]: {
        bg: alpha(theme.palette.error.main, 0.10),
        color: theme.palette.error.main,
      },
    }),
    [theme]
  );

  const getRowId = (application: PopulatedDesignerApplication) =>
    String((application as any)._id);

  const getDesignerId = (application: PopulatedDesignerApplication) => {
    const maybe = application?.designerId;
    
    if (maybe && typeof maybe === "object" && "firstName" in maybe) {
      // already populated, get the _id
      const result = (maybe as any)._id?.toString();
      if (result) {
        return result;
      }
    }
    
    // If designerId is null, try to find the designer by matching user data
    if (!maybe) {
      const user = getUserForApp(application);
      if (user && user._id) {
        const result = user._id.toString();
        return result;
      }
    }
    
    // not populated, convert to string
    const result = String(maybe || "");
    return result;
  };

  const formatTextValue = (value: string | number | null | undefined) => {
    if (value === null || value === undefined) {
      return "-";
    }
    if (typeof value === "string" && value.trim().length === 0) {
      return "-";
    }
    return value;
  };

  const formatEnumValue = (value?: string | null) => {
    if (!value) return "-";
    return value.replace(/_/g, " ").replace(/([a-z])([A-Z])/g, "$1 $2");
  };

  const formatDateValue = (value?: string | Date | null) => {
    if (!value) return "-";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      if (typeof value === "string" && value.trim().length > 0) {
        return value;
      }
      return "-";
    }
    return date.toLocaleDateString();
  };

  const renderLabelValue = (label: string, value: string | number | null | undefined) => (
    <Stack direction="row" spacing={0.75} alignItems="flex-start" flexWrap="wrap">
      <Typography variant="body2" fontWeight={600} sx={{ minWidth: 120 }}>
        {label}:
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
        {formatTextValue(value)}
      </Typography>
    </Stack>
  );

  const visibleColumns = useMemo(
    () => columns.filter((col) => col.visible),
    [columns]
  );
  const allVisibleSelected =
    visibleRows.length > 0 &&
    visibleRows.every((app) => selectedIds.has(getRowId(app)));
  const someVisibleSelected = visibleRows.some((app) => selectedIds.has(getRowId(app)));

  const handleToggleRow = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const handleToggleAll = (checked: boolean) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      visibleRows.forEach((app) => {
        const id = getRowId(app);
        if (checked) {
          next.add(id);
        } else {
          next.delete(id);
        }
      });
      return next;
    });
  };

  const handleSectionToggle = (sectionId: string) => {
    setExpandedSection((prev) => (prev === sectionId ? null : sectionId));
  };

  const handleSortRequest = (columnId: SortableColumn) => {
    setSortConfig((prev) => {
      if (prev?.column === columnId) {
        if (prev.direction === "asc") {
          return { column: columnId, direction: "desc" };
        }
        return null;
      }
      return { column: columnId, direction: "asc" };
    });
    setPage(1);
    setSelectedIds(new Set());
  };

  const handlePageChange = useCallback((_: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
    setSelectedIds(new Set());
  }, []);

  const handleExport = () => {
    // If no rows are selected, export all applications (backward compatibility)
    // If rows are selected, only export selected applications
    const applicationsToExport = selectedIds.size > 0 
      ? allApplications.filter(app => selectedIds.has(getRowId(app)))
      : allApplications;
    
    const validApplications = applicationsToExport.filter(Boolean) as AppWithDates[];

    if (!validApplications.length) {
      const message = selectedIds.size > 0 
        ? "No selected applications available to export."
        : "No applications available to export.";
      showNotification({
        type: 'warning',
        message: message,
      });
      return;
    }

    const sanitizeValue = (value: string | number | null | undefined) => {
      if (value === null || value === undefined) return "";
      const str = String(value);
      if (!str.trim() || str.trim() === "-") return "";
      return str;
    };

    const escapeForCsv = (value: string) => {
      const normalized = value.replace(/\r?\n|\r/g, " ");
      const safe = normalized.replace(/"/g, '""');
      return `"${safe}"`;
    };

    const headers = [
      "Application ID",
      "Status",
      "Membership Tier",
      "Single Payment Items",
      "Designer First Name",
      "Designer Last Name",
      "Designer Email",
      "Designer Phone",
      "Business Name",
      "Business City",
      "Business State",
      "Education",
      "Experience Count",
      "Experience Details",
      "References Count",
      "References Details",
      "Business Goals Overview",
      "Business Goals Capacity",
      "Business Goals Expectations",
      "Business Goals Challenges",
      "Submitted At",
      "Created At",
      "Updated At",
    ];

    const rows = validApplications.map((app) => {
      const user = getUserForApp(app);

      const experiences = (app.experience ?? []).filter(Boolean) as WorkExperience[];
      const references = (app.references ?? []).filter(Boolean) as Reference[];
      const educationList = (app.education ?? []).filter(
        (entry): entry is string => typeof entry === "string" && entry.trim().length > 0
      );
      const singlePaymentItems = (app.singlePaymentItems ?? [])
        .map((item) => (item == null ? "" : String(item).trim()))
        .filter((value) => value.length > 0)
        .map((value) => sanitizeValue(formatEnumValue(value)))
        .filter(Boolean)
        .join("; ");

      const experienceDetails = experiences
        .map((exp) => {
          const titleCompany = [sanitizeValue(exp.title), sanitizeValue(exp.company)]
            .filter(Boolean)
            .join(" @ ");
          const start = sanitizeValue(formatDateValue(exp.startDate ?? null));
          const end = exp.current ? "Current" : sanitizeValue(formatDateValue(exp.endDate ?? null));
          const dateRange = [start, end].filter(Boolean).join(" - ");
          const metaParts = [
            sanitizeValue(formatEnumValue(exp.employmentType ?? null)),
            sanitizeValue(formatEnumValue(exp.firmSize ?? null)),
          ].filter(Boolean);
          return [
            titleCompany,
            dateRange ? `(${dateRange})` : "",
            metaParts.length ? metaParts.join(" | ") : "",
          ]
            .filter(Boolean)
            .join(" ");
        })
        .filter(Boolean)
        .join(" | ");

      const referencesDetails = references
        .map((ref) => {
          const name = `${sanitizeValue(ref.firstName)} ${sanitizeValue(ref.lastName)}`.trim();
          const contactParts = [sanitizeValue(ref.email), sanitizeValue(ref.phoneNumber)].filter(Boolean);
          const dateRange = [
            sanitizeValue(formatDateValue(ref.startDate ?? null)),
            sanitizeValue(formatDateValue(ref.endDate ?? null)),
          ]
            .filter(Boolean)
            .join(" - ");
          const metaParts = [
            sanitizeValue(formatEnumValue(ref.type)),
            sanitizeValue(formatEnumValue((ref as any)?.ProjectType ?? null)),
          ].filter(Boolean);
          return [
            name,
            contactParts.length ? contactParts.join(" / ") : "",
            dateRange ? `(${dateRange})` : "",
            metaParts.length ? metaParts.join(" | ") : "",
          ]
            .filter(Boolean)
            .join(" ");
        })
        .filter(Boolean)
        .join(" | ");

      const businessGoals = app.businessGoals as BusinessGoals | null | undefined;
      const overviewParts: string[] = [];
      if (businessGoals?.overview?.numberOfProjectsLastYear !== undefined &&
          businessGoals?.overview?.numberOfProjectsLastYear !== null) {
        overviewParts.push(
          `Projects Last Year: ${businessGoals.overview.numberOfProjectsLastYear}`
        );
      }
      if (businessGoals?.overview?.numberOfProjectsCurrentYear !== undefined &&
          businessGoals?.overview?.numberOfProjectsCurrentYear !== null) {
        overviewParts.push(
          `Projects Current Year: ${businessGoals.overview.numberOfProjectsCurrentYear}`
        );
      }
      if (sanitizeValue(businessGoals?.overview?.totalRevenueLastYear)) {
        overviewParts.push(
          `Revenue Last Year: ${sanitizeValue(businessGoals?.overview?.totalRevenueLastYear)}`
        );
      }
      if (sanitizeValue(businessGoals?.overview?.totalRevenueCurrentYear)) {
        overviewParts.push(
          `Revenue Current Year: ${sanitizeValue(businessGoals?.overview?.totalRevenueCurrentYear)}`
        );
      }

      const capacityParts: string[] = [];
      if (sanitizeValue(formatEnumValue(businessGoals?.projectCapacity?.smallScale ?? null))) {
        capacityParts.push(
          `Small Scale: ${sanitizeValue(
            formatEnumValue(businessGoals?.projectCapacity?.smallScale ?? null)
          )}`
        );
      }
      if (sanitizeValue(formatEnumValue(businessGoals?.projectCapacity?.largeScale ?? null))) {
        capacityParts.push(
          `Large Scale: ${sanitizeValue(
            formatEnumValue(businessGoals?.projectCapacity?.largeScale ?? null)
          )}`
        );
      }

      const statusValue = app.status as DesignerApplicationSteps | undefined;
      const statusLabel = statusValue ? statusLabels[statusValue] ?? statusValue : "";

      return [
        sanitizeValue(getRowId(app)),
        sanitizeValue(statusLabel),
        sanitizeValue(formatEnumValue(app.membershipTier ?? null)),
        sanitizeValue(singlePaymentItems),
        sanitizeValue(user?.firstName),
        sanitizeValue(user?.lastName),
        sanitizeValue(user?.email),
        sanitizeValue(user?.phoneNumber),
        sanitizeValue((user as any)?.businessName),
        sanitizeValue((user as any)?.businessCity),
        sanitizeValue((user as any)?.businessState),
        sanitizeValue(educationList.join("; ")),
        String(experiences.length),
        sanitizeValue(experienceDetails),
        String(references.length),
        sanitizeValue(referencesDetails),
        sanitizeValue(overviewParts.join(" | ")),
        sanitizeValue(capacityParts.join(" | ")),
        sanitizeValue(businessGoals?.expectations),
        sanitizeValue(businessGoals?.challenges),
        sanitizeValue(formatDateValue(app.submittedAt ?? null)),
        sanitizeValue(formatDateValue(app.createdAt ?? null)),
        sanitizeValue(formatDateValue(app.updatedAt ?? null)),
      ];
    });

    const csvContent = [headers, ...rows]
      .map((row) => row.map((cell) => escapeForCsv(cell)).join(","))
      .join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    const timestamp = new Date().toISOString().split("T")[0];
    const exportType = selectedIds.size > 0 ? `selected-${selectedIds.size}` : 'all';
    link.setAttribute("download", `designer-applications-${exportType}-${timestamp}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    // Show success notification with export details
    const exportMessage = selectedIds.size > 0 
      ? `Exported ${validApplications.length} selected designer applications`
      : `Exported ${validApplications.length} designer applications`;
    
    showNotification({
      type: 'success',
      message: exportMessage,
    });
  };

  const totalCount = sortedApplications.length;
  const startRow = totalCount ? (page - 1) * PAGE_SIZE + 1 : 0;
  const endRow = totalCount
    ? Math.min(totalCount, startRow + visibleRows.length - 1)
    : 0;

  const selectedDesigner = useMemo(() => {
    if (!selectedApplication) return undefined;
    return getUserForApp(selectedApplication);
  }, [selectedApplication]);

  const renderCellContent = (column: ColumnConfig, application: PopulatedDesignerApplication) => {
    const app = application as AppWithDates;
    const u = getUserForApp(app);
    const statusValue = application.status as DesignerApplicationSteps | undefined;
    switch (column.id) {
      case "designerName":
        return `${u?.firstName || ""} ${u?.lastName || ""}`.trim() || "-";
      case "companyName":
        return u?.businessName || "-";
      case "city":
        return (u as any)?.businessCity || "-";
      case "state":
        return (u as any)?.businessState || "-";
      case "status": {
        if (!statusValue) return "-";
        return (
          <Chip
            size="small"
            label={statusLabels[statusValue] ?? statusValue}
            sx={{
              fontWeight: 500,
              py: 0.5, // 8px
              px: 0.5, // 8px
              minWidth: 'auto',
              width: 'fit-content',
              borderRadius: 50,
              backgroundColor: statusBadgeStyles[statusValue]?.bg,
              color: statusBadgeStyles[statusValue]?.color,
            }}
            variant="filled"
          />
        );
      }
      case "subscription": {
        const subscription = (app as any)?.membershipTier?.value || (app as any)?.membershipTier || "-";
        return subscription !== "-" ? subscription : "-";
      }
      case "email":
        return u?.email || "-";
      case "phone":
        return u?.phoneNumber || "-";
      case "accountCreated": {
        // Prioritize profile creation date (user.createdAt) over application creation date
        const profileCreated = u?.createdAt;
        const accountCreated = profileCreated || app.createdAt;
        return accountCreated ? formatDateValue(accountCreated) : "-";
      }
      case "submittedDate": {
        const submitted = app.submittedAt ?? app.createdAt ?? app.updatedAt;
        // Debug logging for first few applications
        return submitted ? new Date(String(submitted)).toLocaleDateString() : "-";
      }
      case "yearsExperience":
        return (app as any)?.yearsOfExperience ?? "-";
      case "portfolioUrl":
        return (app as any)?.portfolioUrl ? (
          <Button size="small" href={(app as any).portfolioUrl} target="_blank">
            View
          </Button>
        ) : (
          "-"
        );
      case "actions":
        return (
          <Stack direction="row" spacing={1} justifyContent="center">
            <Tooltip title="Preview">
              <IconButton
                onClick={() => setSelectedApplication(application as AppWithDates)}
                sx={{
                  width: 36,
                  height: 36,
                  backgroundColor: 'transparent',
                  color: 'rgba(0, 0, 0, 0.8)',
                  borderRadius: 8,
                  '&:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.04)',
                    color: theme.palette.grey[800],
                  },
                }}
              >
                <VisibilityIcon fontSize="small" />
              </IconButton>
            </Tooltip>

            <Tooltip title={
              statusValue === DesignerApplicationSteps.Approved 
                ? "Already Approved" 
                : statusValue !== DesignerApplicationSteps.UnderReview 
                  ? "Only available for applications under review" 
                  : "Approve Application"
            }>
              <IconButton
                onClick={() => handleStatusChange(application, DesignerApplicationSteps.Approved)}
                disabled={
                  statusValue === DesignerApplicationSteps.Approved ||
                  statusValue !== DesignerApplicationSteps.UnderReview
                }
                sx={{
                  width: 36,
                  height: 36,
                  backgroundColor: 'transparent',
                  color: (statusValue === DesignerApplicationSteps.Approved || statusValue !== DesignerApplicationSteps.UnderReview) 
                    ? 'rgba(0, 0, 0, 0.3)' 
                    : theme.palette.success.main,
                  borderRadius: 8,
                  '&:hover': {
                    backgroundColor: (statusValue === DesignerApplicationSteps.Approved || statusValue !== DesignerApplicationSteps.UnderReview) 
                      ? 'transparent' 
                      : 'rgba(76, 175, 80, 0.08)',
                    color: (statusValue === DesignerApplicationSteps.Approved || statusValue !== DesignerApplicationSteps.UnderReview) 
                      ? 'rgba(0, 0, 0, 0.3)' 
                      : theme.palette.success.dark,
                  },
                }}
              >
                <CheckIcon fontSize="small" />
              </IconButton>
            </Tooltip>

            <Tooltip title={
              statusValue === DesignerApplicationSteps.Rejected 
                ? "Already Rejected" 
                : statusValue !== DesignerApplicationSteps.UnderReview 
                  ? "Only available for applications under review" 
                  : "Reject Application"
            }>
              <IconButton
                onClick={() => handleStatusChange(application, DesignerApplicationSteps.Rejected)}
                disabled={
                  statusValue === DesignerApplicationSteps.Rejected ||
                  statusValue !== DesignerApplicationSteps.UnderReview
                }
                sx={{
                  width: 36,
                  height: 36,
                  backgroundColor: 'transparent',
                  color: (statusValue === DesignerApplicationSteps.Rejected || statusValue !== DesignerApplicationSteps.UnderReview) 
                    ? 'rgba(0, 0, 0, 0.3)' 
                    : theme.palette.error.main,
                  borderRadius: 8,
                  '&:hover': {
                    backgroundColor: (statusValue === DesignerApplicationSteps.Rejected || statusValue !== DesignerApplicationSteps.UnderReview) 
                      ? 'transparent' 
                      : 'rgba(244, 67, 54, 0.08)',
                    color: (statusValue === DesignerApplicationSteps.Rejected || statusValue !== DesignerApplicationSteps.UnderReview) 
                      ? 'rgba(0, 0, 0, 0.3)' 
                      : theme.palette.error.dark,
                  },
                }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Tooltip>

            <Tooltip title="Delete">
              <IconButton
                onClick={() => {
                  const user = getUserForApp(application);
                  const name = `${user?.firstName || ""} ${user?.lastName || ""}`.trim() || "Unknown";
                  handleDeleteAccount(getRowId(application), name);
                }}
                sx={{
                  width: 36,
                  height: 36,
                  backgroundColor: 'transparent',
                  color: 'rgba(0, 0, 0, 0.6)',
                  borderRadius: 8,
                  '&:hover': {
                    backgroundColor: 'rgba(244, 67, 54, 0.08)',
                    color: theme.palette.error.main,
                  },
                }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        );
      default:
        return "-";
    }
  };

  const handleStatusFilterClick = (value: StatusFilterValue) => {
    if (value === "ALL") {
      setSelectedStatus("ALL");
      return;
    }
    setSelectedStatus((prev) => (prev === value ? "ALL" : value));
  };

  const paginationSlots = useMemo(
    () => ({
      previous: PrevPaginationSlot,
      next: NextPaginationSlot,
    }),
    []
  );

  const renderPaginationItem = useCallback(
    (item: PaginationRenderItemParams) => {
      if (item.type === "previous" || item.type === "next") {
        return (
          <PaginationItem
            {...item}
            slots={paginationSlots}
            sx={{
              borderRadius: 2,
              minWidth: 110,
              height: 40,
              px: 2.25,
              fontWeight: 500,
              border: `1px solid ${theme.palette.grey[200]}`,
              backgroundColor: theme.palette.common.white,
              color: theme.palette.text.primary,
              '&:hover': {
                borderColor: theme.palette.primary.main,
                color: theme.palette.primary.main,
                backgroundColor: alpha(theme.palette.primary.main, 0.08),
              },
              '&.Mui-disabled': {
                borderColor: theme.palette.grey[100],
                color: theme.palette.text.disabled,
              },
            }}
          />
        );
      }

      if (item.type === "start-ellipsis" || item.type === "end-ellipsis") {
        return (
          <PaginationItem
            {...item}
            sx={{
              borderRadius: 2,
              minWidth: 44,
              height: 40,
              px: 1.5,
              border: `1px solid ${theme.palette.grey[100]}`,
              backgroundColor: theme.palette.common.white,
              color: theme.palette.text.secondary,
              pointerEvents: "none",
            }}
            disabled
          />
        );
      }

      if (item.type === "page") {
        return (
          <PaginationItem
            {...item}
            sx={{
              borderRadius: 2,
              minWidth: 44,
              height: 40,
              px: 1.5,
              fontWeight: 600,
              fontSize: 14,
              border: `1px solid ${
                item.selected ? theme.palette.primary.main : theme.palette.grey[200]
              }`,
              backgroundColor: item.selected
                ? alpha(theme.palette.primary.main, 0.12)
                : theme.palette.common.white,
              color: item.selected ? theme.palette.primary.main : theme.palette.text.primary,
              transition: theme.transitions.create(
                ["border-color", "color", "background-color"],
                { duration: theme.transitions.duration.shortest }
              ),
              '&:hover': {
                borderColor: theme.palette.primary.main,
                color: theme.palette.primary.main,
                backgroundColor: alpha(theme.palette.primary.main, 0.08),
              },
            }}
          >
            {String(item.page).padStart(2, "0")}
          </PaginationItem>
        );
      }

      return <PaginationItem {...item} />;
    },
    [paginationSlots, theme]
  );

  const experiences = useMemo(
    () =>
      (selectedApplication?.experience ?? []).filter(Boolean) as WorkExperience[],
    [selectedApplication]
  );

  const educationList = useMemo(
    () =>
      (selectedApplication?.education ?? []).filter(
        (item): item is string => typeof item === "string" && item.trim().length > 0
      ),
    [selectedApplication]
  );

  const references = useMemo(
    () => {
      const rawReferences = selectedApplication?.references;
      // Handle array-like objects defensively (legacy format: object keyed by id)
      const refsArray = Array.isArray(rawReferences)
        ? rawReferences
        : rawReferences && typeof rawReferences === "object"
          ? Object.values(rawReferences)
          : [];
      const refs = refsArray.filter(Boolean) as Reference[];
      // Debug logging for references
      return refs;
    },
    [selectedApplication]
  );

  const businessGoals = useMemo(() => {
    const rawGoals = selectedApplication?.businessGoals;
    let goals = rawGoals;
    // Handle JSON string format (legacy)
    if (rawGoals && typeof rawGoals === "string") {
      try {
        goals = JSON.parse(rawGoals);
      } catch {
        goals = null;
      }
    }
    // Normalize object format
    if (goals && typeof goals === "object" && !Array.isArray(goals)) {
      goals = { ...goals };
    } else if (!goals || typeof goals !== "object") {
      goals = null;
    }
    const normalizedGoals = goals as BusinessGoals | null;
    // Debug logging for businessGoals
    return normalizedGoals;
  }, [selectedApplication]);

  const drawerSections = useMemo(() => {
    if (!selectedApplication) return [];

    const experienceContent = experiences.length ? (
      <Stack spacing={2}>
        {experiences.map((exp) => (
          <Box
            key={exp.id || `${exp.company}-${exp.startDate}-${exp.endDate}`}
            sx={{
              borderRadius: 2,
              border: (theme) => `1px solid ${theme.palette.grey[200]}`,
              p: 2,
              backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.03),
            }}
          >
            <Stack spacing={1}>
              {renderLabelValue("ID", exp.id ?? "-")}
              {renderLabelValue("Title", exp.title)}
              {renderLabelValue("Company", exp.company)}
              {renderLabelValue("Start Date", formatDateValue(exp.startDate ?? null))}
              {renderLabelValue("End Date", formatDateValue(exp.endDate ?? null))}
              {renderLabelValue(
                "Employment Status",
                typeof exp.current === "boolean" ? (exp.current ? "Current" : "Previous") : null
              )}
              {renderLabelValue("Employment Type", formatEnumValue(exp.employmentType ?? null))}
              {renderLabelValue("Firm Size", formatEnumValue(exp.firmSize ?? null))}
            </Stack>
          </Box>
        ))}
      </Stack>
    ) : (
      <Typography variant="body2" color="text.secondary">
        No experience details provided.
      </Typography>
    );

    const educationContent = educationList.length ? (
      <Stack spacing={1.5}>
        {educationList.map((entry, index) => (
          <Stack key={`${entry}-${index}`} spacing={0.5}>
            <Typography variant="body2" color="text.secondary">
              {entry}
            </Typography>
          </Stack>
        ))}
      </Stack>
    ) : (
      <Typography variant="body2" color="text.secondary">
        No education details provided.
      </Typography>
    );

    const referenceContent = references.length ? (
      <Stack spacing={2}>
        {references.map((ref) => (
          <Box
            key={ref.id}
            sx={{
              borderRadius: 2,
              border: (theme) => `1px solid ${theme.palette.grey[200]}`,
              p: 2,
              backgroundColor: (theme) => alpha(theme.palette.secondary.main, 0.02),
            }}
          >
            <Stack spacing={1}>
              {renderLabelValue("Name", `${ref.firstName} ${ref.lastName}`.trim())}
              {renderLabelValue("Title", ref.title)}
              {renderLabelValue("Type", formatEnumValue(ref.type))}
              {renderLabelValue("Business Name", ref.businessName)}
              {renderLabelValue("Email", ref.email)}
              {renderLabelValue("Phone", ref.phoneNumber)}
              {renderLabelValue("Start Date", formatDateValue(ref.startDate ?? null))}
              {renderLabelValue("End Date", formatDateValue(ref.endDate ?? null))}
              {renderLabelValue(
                "Years Known",
                typeof ref.yearsKnown === "number" ? ref.yearsKnown.toString() : ref.yearsKnown
              )}
              {renderLabelValue(
                "Project Type",
                formatEnumValue((ref as any)?.ProjectType ?? null)
              )}
            </Stack>
          </Box>
        ))}
      </Stack>
    ) : (
      <Typography variant="body2" color="text.secondary">
        No references provided.
      </Typography>
    );

    const businessGoalsContent = businessGoals ? (
      <Stack spacing={2}>
        <Stack spacing={1}>
          <Typography variant="subtitle2" fontWeight={600}>
            Overview
          </Typography>
          {renderLabelValue(
            "Projects (Last Year)",
            businessGoals.overview?.numberOfProjectsLastYear ?? null
          )}
          {renderLabelValue(
            "Projects (Current Year)",
            businessGoals.overview?.numberOfProjectsCurrentYear ?? null
          )}
          {renderLabelValue(
            "Revenue (Last Year)",
            businessGoals.overview?.totalRevenueLastYear ?? null
          )}
          {renderLabelValue(
            "Revenue (Current Year)",
            businessGoals.overview?.totalRevenueCurrentYear ?? null
          )}
        </Stack>
        <Stack spacing={1}>
          <Typography variant="subtitle2" fontWeight={600}>
            Project Capacity
          </Typography>
          {renderLabelValue(
            "Small Scale",
            formatEnumValue(businessGoals.projectCapacity?.smallScale ?? null)
          )}
          {renderLabelValue(
            "Large Scale",
            formatEnumValue(businessGoals.projectCapacity?.largeScale ?? null)
          )}
        </Stack>
        <Stack spacing={1}>
          <Typography variant="subtitle2" fontWeight={600}>
            Expectations
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {formatTextValue(businessGoals.expectations)}
          </Typography>
        </Stack>
        <Stack spacing={1}>
          <Typography variant="subtitle2" fontWeight={600}>
            Challenges
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {formatTextValue(businessGoals.challenges)}
          </Typography>
        </Stack>
      </Stack>
    ) : (
      <Typography variant="body2" color="text.secondary">
        No business goals provided.
      </Typography>
    );

    const basicInfoContent = selectedDesigner ? (
      <Stack spacing={1.5}>
        {renderLabelValue("First Name", selectedDesigner.firstName)}
        {renderLabelValue("Last Name", selectedDesigner.lastName)}
        {renderLabelValue("Email", selectedDesigner.email)}
        {renderLabelValue("Phone Number", selectedDesigner.phoneNumber)}
        {renderLabelValue("Business Name", selectedDesigner.businessName)}
        {renderLabelValue("Business Address", (selectedDesigner as any).businessAddress)}
        {renderLabelValue("Business City", (selectedDesigner as any).businessCity)}
        {renderLabelValue("Business State", (selectedDesigner as any).businessState)}
        {renderLabelValue("Business Zip Code", (selectedDesigner as any).businessZipCode)}
      </Stack>
    ) : (
      <Typography variant="body2" color="text.secondary">
        No basic information available.
      </Typography>
    );

    return [
      {
        id: "basic",
        label: "Basic Information",
        icon: <PersonOutlinedIcon fontSize="small" />,
        content: basicInfoContent,
      },
      {
        id: "experience",
        label: "Experience",
        icon: <WorkOutlineOutlinedIcon fontSize="small" />,
        content: experienceContent,
      },
      {
        id: "education",
        label: "Education",
        icon: <SchoolOutlinedIcon fontSize="small" />,
        content: educationContent,
      },
      {
        id: "references",
        label: "References",
        icon: <ContactPageOutlinedIcon fontSize="small" />,
        content: referenceContent,
      },
      {
        id: "businessGoals",
        label: "Business Goals",
        icon: <FlagOutlinedIcon fontSize="small" />,
        content: businessGoalsContent,
      },
    ];
  }, [selectedApplication, experiences, educationList, references, businessGoals]);

  const designerDisplayName = useMemo(() => {
    if (!selectedDesigner) return "Application Details";
    const fullName = `${selectedDesigner.firstName ?? ""} ${selectedDesigner.lastName ?? ""}`.trim();
    if (fullName.length > 0) return fullName;
    return selectedDesigner.email ?? "Application Details";
  }, [selectedDesigner]);

  const designerSubheading = useMemo(() => {
    if (!selectedDesigner) return "";
    if (selectedDesigner.businessName) return selectedDesigner.businessName;
    const maybeCity = (selectedDesigner as any)?.businessCity;
    const maybeState = (selectedDesigner as any)?.businessState;
    const locationParts = [maybeCity, maybeState]
      .map((value) => (typeof value === "string" ? value.trim() : ""))
      .filter((value) => value.length > 0);
    if (locationParts.length) return locationParts.join(", ");
    return selectedDesigner.email ?? "";
  }, [selectedDesigner]);

  return (
    <Stack spacing={3} sx={{ width: "100%" }}>
      <Paper
        className="admin-table-wrapper"
        elevation={0}
        sx={{
          p: { xs: 3, md: 4 },
          borderRadius: 4,
          border: `1px solid ${theme.palette.grey[200]}`,
          backgroundColor: "#fcfdff",
          overflowX: "auto",
        }}
      >
        {loading ? (
          <Box display="flex" justifyContent="center" p={4}>
            <CircularProgress sx={{ color: '#00A06D' }} />
          </Box>
        ) : (
          <>
            <ErrorMessage message={designerListError?.message || error} />

            <Stack
              direction={{ xs: "column", md: "row" }}
              justifyContent="space-between"
              alignItems={{ xs: "flex-start", md: "center" }}
              spacing={2}
              sx={{ mb: 3 }}
            >
              <Stack spacing={0.5}>
                <Typography variant="h5" fontWeight={700} sx={{ fontSize: { xs: '1.5rem', md: '1.75rem' } }}>
                  Applications
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.875rem', md: '0.875rem' } }}>
                  Manage all designer applications.
                </Typography>
              </Stack>
              <Stack direction="row" spacing={1} alignItems="center">
                {selectedIds.size > 0 && (
                  <Button
                    variant="outlined"
                    color="error"
                    size="small"
                    startIcon={<DeleteIcon />}
                    onClick={() => {
                      const selectedNames = Array.from(selectedIds)
                        .map(id => {
                          const app = allApplications.find(a => getRowId(a) === id);
                          if (app) {
                            const user = getUserForApp(app);
                            return `${user?.firstName || ""} ${user?.lastName || ""}`.trim() || "Unknown";
                          }
                          return "Unknown";
                        })
                        .join(", ");
                      const selectedDesignerIds = Array.from(selectedIds)
                        .map(id => {
                          const app = allApplications.find(a => getRowId(a) === id);
                          return app ? getDesignerId(app) : null;
                        })
                        .filter(Boolean)
                        .join(",");
                      setDeleteDialog({
                        open: true,
                        type: "account",
                        applicationId: selectedDesignerIds,
                        applicationName: selectedNames,
                      });
                    }}
                    sx={{ 
                      borderRadius: 2, 
                      textTransform: "none",
                      fontSize: { xs: '0.75rem', md: '0.875rem' },
                    }}
                  >
                    Delete Selected ({selectedIds.size})
                  </Button>
                )}
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<DownloadOutlinedIcon />}
                  onClick={handleExport}
                  sx={{ 
                    borderRadius: 2, 
                    textTransform: "none",
                    fontSize: { xs: '0.75rem', md: '0.875rem' },
                  }}
                >
                  {selectedIds.size > 0 
                    ? `Export Selected (${selectedIds.size}) as CSV`
                    : 'Export as CSV'
                  }
                </Button>
                <IconButton
                  sx={{
                    border: (theme) => `1px solid ${theme.palette.grey[200]}`,
                    borderRadius: 2,
                    color: "text.secondary",
                  }}
                >
                  <MoreHorizIcon />
                </IconButton>
              </Stack>
            </Stack>

            <Stack spacing={2} sx={{ mb: 2 }}>
              {/* Status Filter Chips with Horizontal Scroll */}
              <Box
                sx={{
                  overflowX: 'auto',
                  overflowY: 'hidden',
                  '&::-webkit-scrollbar': {
                    height: 4,
                  },
                  '&::-webkit-scrollbar-track': {
                    backgroundColor: theme.palette.grey[100],
                    borderRadius: 2,
                  },
                  '&::-webkit-scrollbar-thumb': {
                    backgroundColor: theme.palette.grey[400],
                    borderRadius: 2,
                    '&:hover': {
                      backgroundColor: theme.palette.grey[600],
                    },
                  },
                }}
              >
                <Stack 
                  direction="row" 
                  spacing={1} 
                  sx={{ 
                    minWidth: 'max-content',
                    pb: 1,
                  }}
                >
                  {STATUS_FILTERS.map(({ value, label }) => (
                    <Chip
                      key={String(value)}
                      label={`${label} (${statusCounts[value] ?? 0})`}
                      onClick={() => handleStatusFilterClick(value)}
                      clickable
                      variant="outlined"
                      size="small"
                      sx={{
                        py: 0.5, // 8px
              px: 0.5, // 8px
              minWidth: 'auto',
              width: 'fit-content',
              borderRadius: 50,
                        fontWeight: 600,
                        fontSize: { xs: '0.75rem', md: '0.875rem' },
                        height: { xs: 28, md: 32 },
                        backgroundColor:
                          selectedStatus === value
                            ? theme.palette.common.black
                            : theme.palette.common.white,
                        color:
                          selectedStatus === value
                            ? theme.palette.common.white
                            : theme.palette.text.primary,
                        borderColor:
                          selectedStatus === value
                            ? theme.palette.common.black
                            : theme.palette.grey[300],
                        '&:hover': {
                          backgroundColor:
                            selectedStatus === value
                              ? 'transparent'
                              : alpha(theme.palette.common.black, 0.05),
                          color:
                            selectedStatus === value
                              ? theme.palette.common.black
                              : theme.palette.text.primary,
                          borderColor: theme.palette.common.black,
                        },
                        transition: theme.transitions.create(
                          ["background-color", "color", "border-color"],
                          { duration: theme.transitions.duration.shortest }
                        ),
                      }}
                    />
                  ))}
                </Stack>
              </Box>

              {/* Search and Controls - Stacked on Mobile */}
              <Stack
                direction={{ xs: "column", md: "row" }}
                spacing={{ xs: 1.5, md: 2 }}
                alignItems={{ xs: "stretch", md: "center" }}
                justifyContent={{ xs: "flex-start", md: "space-between" }}
              >
                {/* Search Bar - Full Width on Mobile */}
                <TextField
                  value={searchTerm}
                  onChange={(event) => setSearchTerm(event.target.value)}
                  placeholder="Search across all data (name, email, business, experience, references, etc.)"
                  size="small"
                  variant="outlined"
                  sx={{
                    width: { xs: "100%", md: 240 },
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 20,
                    },
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" color="action" />
                      </InputAdornment>
                    ),
                  }}
                />

                {/* Controls Row */}
                <Stack 
                  direction="row" 
                  spacing={1} 
                  alignItems="center"
                  justifyContent={{ xs: "space-between", md: "flex-end" }}
                >
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={(e) => setColumnMenuAnchor(e.currentTarget)}
                    startIcon={<ViewColumnIcon />}
                    sx={{ 
                      textTransform: "none", 
                      borderRadius: 2,
                      fontSize: { xs: '0.75rem', md: '0.875rem' },
                    }}
                  >
                    Columns
                  </Button>
                  <Button
                    variant="text"
                    size="small"
                    onClick={() => loadAllApplications(true)}
                    startIcon={<AutorenewIcon />}
                    sx={{ 
                      textTransform: "none", 
                      borderRadius: 2,
                      fontSize: { xs: '0.75rem', md: '0.875rem' },
                    }}
                  >
                    Refresh
                  </Button>
                </Stack>
              </Stack>
            </Stack>

            {/* Column Selection Menu */}
            <Menu
              anchorEl={columnMenuAnchor}
              open={Boolean(columnMenuAnchor)}
              onClose={() => setColumnMenuAnchor(null)}
            >
              <Box sx={{ p: 2, minWidth: 200 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Select Columns
                </Typography>
                <FormGroup>
                  {columns
                    .filter((col) => col.id !== "actions")
                    .map((column) => (
                      <FormControlLabel
                        key={column.id}
                        control={
                          <Checkbox
                            checked={column.visible}
                            onChange={() => handleColumnToggle(column.id)}
                            size="small"
                          />
                        }
                        label={column.label}
                      />
                    ))}
                </FormGroup>
                <Divider sx={{ my: 1 }} />
                <Button variant="contained" size="small" fullWidth onClick={handleApplyColumns}>
                  Apply
                </Button>
              </Box>
            </Menu>

            <Box
              sx={{
                overflowX: 'auto',
                overflowY: 'hidden',
                '&::-webkit-scrollbar': {
                  height: 6,
                },
                '&::-webkit-scrollbar-track': {
                  backgroundColor: theme.palette.grey[100],
                  borderRadius: 3,
                },
                '&::-webkit-scrollbar-thumb': {
                  backgroundColor: theme.palette.grey[400],
                  borderRadius: 3,
                  '&:hover': {
                    backgroundColor: theme.palette.grey[600],
                  },
                },
              }}
            >
              <Table 
                className="admin-dashboard-table" 
                size="small"
                sx={{
                  minWidth: { xs: 800, md: 'auto' }, // Ensure minimum width for mobile
                  "& .MuiTableCell-root": {
                    fontSize: { xs: '0.75rem', md: '0.875rem' },
                    whiteSpace: { xs: 'nowrap', md: 'normal' },
                  },
                  "& .MuiTableHead-root .MuiTableCell-root": {
                    fontSize: { xs: '0.75rem', md: '0.875rem' },
                  },
                }}
              >
                <TableHead className="header">
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Checkbox
                        size="small"
                        indeterminate={someVisibleSelected && !allVisibleSelected}
                        checked={visibleRows.length > 0 && allVisibleSelected}
                        onChange={(event) => handleToggleAll(event.target.checked)}
                      />
                    </TableCell>
                    {visibleColumns.map((column) => (
                      <TableCell
                        key={column.id}
                        align={column.align || "left"}
                        style={{ minWidth: column.minWidth }}
                      >
                        {column.id === "state" || column.id === "accountCreated" || column.id === "submittedDate" ? (
                          <TableSortLabel
                            active={sortConfig?.column === column.id}
                            direction={sortConfig?.column === column.id ? sortConfig.direction : "asc"}
                            onClick={() => handleSortRequest(column.id as SortableColumn)}
                          >
                            {column.label}
                          </TableSortLabel>
                        ) : (
                          column.label
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {visibleRows.length > 0 ? (
                    visibleRows.map((application) => (
                      <TableRow key={String((application as any)._id)} hover>
                        <TableCell padding="checkbox">
                          <Checkbox
                            size="small"
                            checked={selectedIds.has(getRowId(application))}
                            onChange={() => handleToggleRow(getRowId(application))}
                          />
                        </TableCell>
                        {visibleColumns.map((column) => (
                          <TableCell key={column.id} align={column.align || "left"}>
                            {renderCellContent(column, application)}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={visibleColumns.length + 1} align="center">
                        <Typography color="text.secondary">No applications found</Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </Box>

            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={{ xs: 1, sm: 2 }}
              alignItems={{ xs: "flex-start", sm: "center" }}
              justifyContent={{ xs: "flex-start", sm: "space-between" }}
              sx={{ mt: 3 }}
            >
              <Typography 
                variant="body2" 
                color="text.secondary"
                sx={{ fontSize: { xs: '0.75rem', md: '0.875rem' } }}
              >
                Showing {startRow}-{endRow} of {totalCount.toLocaleString()} applications
              </Typography>
              <Pagination
                shape="rounded"
                count={pageCount}
                page={page}
                onChange={handlePageChange}
                siblingCount={0}
                boundaryCount={1}
                size="small"
                renderItem={renderPaginationItem}
                sx={{
                  '& .MuiPagination-ul': {
                    gap: { xs: 0.25, md: 0.75 },
                  },
                  '& .MuiPaginationItem-root': {
                    fontSize: { xs: '0.75rem', md: '0.875rem' },
                    minWidth: { xs: 28, md: 32 },
                    height: { xs: 28, md: 32 },
                  },
                }}
              />
            </Stack>

          </>
        )}
      </Paper>
      <Drawer
        anchor="right"
        open={Boolean(selectedApplication)}
        onClose={() => setSelectedApplication(null)}
        PaperProps={{
          sx: {
            width: { xs: "100%", sm: 420, md: 500 },
            borderRadius: "16px 0 0 16px",
            boxShadow: 6,
          },
        }}
      >
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{ px: 3, py: 2, borderBottom: (theme) => `1px solid ${theme.palette.grey[200]}` }}
        >
          <Typography 
            variant="h5" 
            fontWeight={700}
            sx={{ fontSize: { xs: '1.25rem', md: '1.5rem' } }}
          >
            {designerDisplayName}
          </Typography>
          <IconButton onClick={() => setSelectedApplication(null)}>
            <ChevronLeftIcon />
          </IconButton>
        </Stack>
        <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
          {selectedApplication ? (
            <>
              <Box sx={{ px: 3, py: 2, flex: 1, overflowY: "auto" }}>
                <Stack spacing={3}>
                  {designerSubheading ? (
                    <Typography variant="subtitle1" color="text.secondary" fontWeight={500}>
                      {designerSubheading}
                    </Typography>
                  ) : null}


                  <Divider sx={{ borderColor: (theme) => theme.palette.grey[200] }} />

                  <Stack spacing={1.5}>
                    {drawerSections.map(({ id, label, icon, content }) => {
                      const isExpanded = expandedSection === id;
                      return (
                        <Box
                          key={id}
                          sx={{
                            borderRadius: 3,
                            border: (theme) => `1px solid ${theme.palette.grey[200]}`,
                            overflow: "hidden",
                          }}
                        >
                          <Box
                            role="button"
                            tabIndex={0}
                            onClick={() => handleSectionToggle(id)}
                            onKeyDown={(event) => {
                              if (event.key === "Enter" || event.key === " ") {
                                event.preventDefault();
                                handleSectionToggle(id);
                              }
                            }}
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              px: 2.25,
                              py: 1.75,
                              cursor: "pointer",
                              backgroundColor: isExpanded
                                ? alpha(theme.palette.primary.main, 0.06)
                                : theme.palette.common.white,
                              transition: "background-color 0.2s ease",
                              '&:hover': {
                                backgroundColor: alpha(theme.palette.primary.main, 0.08),
                              },
                              outline: "none",
                            }}
                          >
                            <Stack direction="row" spacing={1.5} alignItems="center">
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  color: theme.palette.primary.main,
                                }}
                              >
                                {icon}
                              </Box>
                              <Typography variant="subtitle1" fontWeight={600}>
                                {label}
                              </Typography>
                            </Stack>
                            <Stack direction="row" spacing={1} alignItems="center">
                              {isExpanded && (
                                <Tooltip title="Edit this section">
                                  <IconButton
                                    size="small"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleSectionEditClick(id);
                                    }}
                                    sx={{
                                      backgroundColor: 'transparent',
                                      color: 'rgba(0, 0, 0, 0.8)',
                                      borderRadius: 12,
                                      '&:hover': {
                                        backgroundColor: 'transparent',
                                        color: theme.palette.grey[800],
                                      },
                                    }}
                                  >
                                    <EditOutlinedIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              )}
                              {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                            </Stack>
                          </Box>
                          <Collapse in={isExpanded} unmountOnExit>
                            <Box sx={{ px: 2.25, py: 2 }}>
                              {editingSection === id ? (
                                <SectionEditForm
                                  sectionId={id}
                                  editFormData={editFormData}
                                  updateLoading={updateLoading}
                                  onFormChange={handleEditFormChange}
                                  onFormChangeNested={handleEditFormChangeNested}
                                  onFormChangeDeep={handleEditFormChangeDeep}
                                  onAddEducation={addEducationEntry}
                                  onRemoveEducation={removeEducationEntry}
                                  onUpdateEducation={updateEducationEntry}
                                  onAddExperience={addExperienceEntry}
                                  onRemoveExperience={removeExperienceEntry}
                                  onUpdateExperience={updateExperienceEntry}
                                  onAddReference={addReferenceEntry}
                                  onRemoveReference={removeReferenceEntry}
                                  onUpdateReference={updateReferenceEntry}
                                  onSave={handleSectionEditSave}
                                  onCancel={handleSectionEditCancel}
                                />
                              ) : (
                                content
                              )}
                            </Box>
                          </Collapse>
                        </Box>
                      );
                    })}
                  </Stack>
                </Stack>
              </Box>
              <Divider sx={{ borderColor: (theme) => theme.palette.grey[200] }} />
              <Box sx={{ px: 3, py: 2 }}>
                <Button
                  fullWidth
                  variant="text"
                  color="error"
                  onClick={() => {
                    if (selectedApplication) {
                      const user = getUserForApp(selectedApplication);
                      const name = `${user?.firstName || ""} ${user?.lastName || ""}`.trim() || "Unknown";
                      const designerId = getDesignerId(selectedApplication);
                      handleDeleteAccount(getRowId(selectedApplication), name);
                    }
                  }}
                  sx={{ 
                    textTransform: "none", 
                    fontWeight: 400,
                    fontSize: "0.875rem",
                    minHeight: "44px",
                    mt: 2, // 16px gap (2 * 8px = 16px)
                    color: theme.palette.error.main,
                    '&:hover': {
                      backgroundColor: alpha(theme.palette.error.main, 0.08),
                      color: theme.palette.error.dark,
                    }
                  }}
                >
                  Delete Account & Application
                </Button>
              </Box>
            </>
          ) : (
            <Box sx={{ p: 3 }}>
              <Typography variant="body2" color="text.secondary">
                Unable to load application details.
              </Typography>
            </Box>
          )}
        </Box>
      </Drawer>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialog.open}
        onClose={handleCancelDelete}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">
          Confirm Delete
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            {deleteDialog.applicationId?.includes(",") ? (
              <>
                Are you sure you want to delete the selected accounts and applications?
                <br /><br />
                <strong>Accounts to delete:</strong>
                <br />
                {deleteDialog.applicationName}
                <br /><br />
                This will permanently delete the designer accounts and all associated applications. This action cannot be undone.
              </>
            ) : (
              <>
                Are you sure you want to delete the account and application for <strong>{deleteDialog.applicationName}</strong>?
                <br /><br />
                This will permanently delete the designer account and all associated applications. The user will be able to start fresh with a new account. This action cannot be undone.
              </>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDelete} color="primary">
            Cancel
          </Button>
          <Button 
            onClick={handleConfirmDelete} 
            color="error" 
            variant="contained"
            autoFocus
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};
